import { useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Lenis from "lenis";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
// Home is the landing route -- always needed first, so it stays a normal
// (eager) import. Every other page is code-split into its own chunk so
// visiting "/" doesn't have to download the whole site's JS up front.
import Home from "@/pages/Home";
// import About from "@/pages/About";
const Services = lazy(() => import("@/pages/Services"));
// import Industries from "@/pages/Industries";
const Clients = lazy(() => import("@/pages/Clients"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Contact = lazy(() => import("@/pages/Contact"));
const AntimicrobialPvcWallCladding = lazy(() =>
  import("@/pages/services/AntimicrobialPvcWallCladding")
);
const HospitalRefurbishment = lazy(() =>
  import("@/pages/services/HospitalRefurbishment")
);
const PrintedGlassWall = lazy(() => import("@/pages/services/PrintedGlassWall"));
const ContaminationControlFlooring = lazy(() =>
  import("@/pages/services/ContaminationControlFlooring")
);
const HygienicWallCladding = lazy(() =>
  import("@/pages/services/HygienicWallCladding")
);

// Lenis owns its own internal scroll position, separate from the native
// browser scroll. Resetting window.scrollTo alone isn't enough on route
// change -- Lenis keeps animating toward its last remembered position and
// fights the reset back down a frame later. It has to be told to jump too.
const ScrollToTop = ({ lenisRef }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, lenisRef]);
  return null;
};

function AppShell() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <div className="App min-h-screen bg-[#F8FAFC] text-[#0F2A44]">
      <BrowserRouter>
        <ScrollToTop lenisRef={lenisRef} />
        <Navbar />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            {/* <Route path="/about" element={<About />} /> */}
            <Route path="/services" element={<Services />} />
            <Route
              path="/services/antimicrobial-pvc-wall-cladding"
              element={<AntimicrobialPvcWallCladding />}
            />
            <Route
              path="/services/hospital-refurbishment"
              element={<HospitalRefurbishment />}
            />
            <Route
              path="/services/printed-glass-wall"
              element={<PrintedGlassWall />}
            />
            <Route
              path="/services/contamination-control-flooring"
              element={<ContaminationControlFlooring />}
            />
            <Route
              path="/services/hygienic-wall-cladding"
              element={<HygienicWallCladding />}
            />
            {/* <Route path="/industries" element={<Industries />} /> */}
            <Route path="/clients" element={<Clients />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </Suspense>
        <Footer />
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0F2A44",
            color: "#F8FAFC",
            border: "1px solid #189A96",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />
    </div>
  );
}

function App() {
  return <AppShell />;
}

export default App;
